import { useState } from 'react';
import { IntegrationDefinition, AccessFinancialsConfig, FieldMapping, SyncFrequency } from '@/types/integrations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, CheckCircle2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AccessFinancialsDialogProps {
  integration: IntegrationDefinition | null;
  open: boolean;
  onClose: () => void;
  onConnect: (integrationId: string, config: AccessFinancialsConfig) => Promise<void>;
}

const FIELD_MAPPINGS_TEMPLATE: FieldMapping[] = [
  { sourceField: 'CustomerID', destinationField: 'customer_id', transform: '' },
  { sourceField: 'CustomerName', destinationField: 'customer_name', transform: '' },
  { sourceField: 'InvoiceNumber', destinationField: 'invoice_number', transform: '' },
  { sourceField: 'InvoiceDate', destinationField: 'invoice_date', transform: 'date' },
  { sourceField: 'Amount', destinationField: 'amount', transform: 'currency' },
  { sourceField: 'PaymentDate', destinationField: 'payment_date', transform: 'date' },
];

export function AccessFinancialsDialog({
  integration,
  open,
  onClose,
  onConnect,
}: AccessFinancialsDialogProps) {
  const [step, setStep] = useState(1);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  const [config, setConfig] = useState<AccessFinancialsConfig>({
    serverUrl: '',
    database: '',
    username: '',
    password: '',
    dataSyncOptions: {
      customers: true,
      invoices: true,
      payments: true,
      accounts: false,
      transactions: false,
    },
    syncFrequency: 'daily',
    initialSyncDate: new Date().toISOString(),
    fieldMappings: FIELD_MAPPINGS_TEMPLATE,
  });

  const handleClose = () => {
    setStep(1);
    setTesting(false);
    setConnecting(false);
    setTestSuccess(false);
    setConfig({
      serverUrl: '',
      database: '',
      username: '',
      password: '',
      dataSyncOptions: {
        customers: true,
        invoices: true,
        payments: true,
        accounts: false,
        transactions: false,
      },
      syncFrequency: 'daily',
      initialSyncDate: new Date().toISOString(),
      fieldMappings: FIELD_MAPPINGS_TEMPLATE,
    });
    onClose();
  };

  const handleTestConnection = async () => {
    setTesting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setTestSuccess(true);
    setTesting(false);
  };

  const handleAutoMap = () => {
    // Auto-mapping is already done with template
    setConfig({ ...config, fieldMappings: FIELD_MAPPINGS_TEMPLATE });
  };

  const handleConnect = async () => {
    if (!integration) return;

    setConnecting(true);
    try {
      await onConnect(integration.id, config);
      handleClose();
    } catch (error) {
      console.error('Connection failed:', error);
    } finally {
      setConnecting(false);
    }
  };

  const isStep1Valid = () => {
    return config.serverUrl && config.database && config.username && config.password;
  };

  const isStep2Valid = () => {
    return Object.values(config.dataSyncOptions).some(v => v);
  };

  if (!integration) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{integration.logo}</div>
            <div>
              <DialogTitle>Connect {integration.name}</DialogTitle>
              <DialogDescription>
                Step {step} of 4: {
                  step === 1 ? 'Connection Details' :
                  step === 2 ? 'Data Sync Configuration' :
                  step === 3 ? 'Field Mapping' :
                  'Review & Connect'
                }
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-4">
            {/* Step 1: Connection */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="serverUrl">
                    Server URL <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="serverUrl"
                    type="url"
                    placeholder="https://server.example.com"
                    value={config.serverUrl}
                    onChange={(e) => setConfig({ ...config, serverUrl: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="database">
                    Database Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="database"
                    placeholder="AccessDB"
                    value={config.database}
                    onChange={(e) => setConfig({ ...config, database: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">
                    Username <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="username"
                    value={config.username}
                    onChange={(e) => setConfig({ ...config, username: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={config.password}
                    onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  />
                </div>

                {testSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm">Connection successful!</span>
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleTestConnection}
                  disabled={!isStep1Valid() || testing}
                >
                  {testing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
              </>
            )}

            {/* Step 2: Data Sync Configuration */}
            {step === 2 && (
              <>
                <div className="space-y-4">
                  <Label>Select data to sync:</Label>
                  {Object.entries(config.dataSyncOptions).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={key}
                        checked={value}
                        onCheckedChange={(checked) =>
                          setConfig({
                            ...config,
                            dataSyncOptions: {
                              ...config.dataSyncOptions,
                              [key]: checked as boolean,
                            },
                          })
                        }
                      />
                      <Label htmlFor={key} className="font-normal capitalize cursor-pointer">
                        {key}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="syncFrequency">Sync Frequency</Label>
                  <Select
                    value={config.syncFrequency}
                    onValueChange={(value) =>
                      setConfig({ ...config, syncFrequency: value as SyncFrequency })
                    }
                  >
                    <SelectTrigger id="syncFrequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="realtime">Real-time</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Initial Sync Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !config.initialSyncDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {config.initialSyncDate
                          ? format(new Date(config.initialSyncDate), 'PPP')
                          : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={new Date(config.initialSyncDate)}
                        onSelect={(date) =>
                          date && setConfig({ ...config, initialSyncDate: date.toISOString() })
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}

            {/* Step 3: Field Mapping */}
            {step === 3 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <Label>Field Mappings</Label>
                  <Button variant="outline" size="sm" onClick={handleAutoMap}>
                    Auto-map Fields
                  </Button>
                </div>

                <div className="border rounded-md">
                  <div className="grid grid-cols-3 gap-4 p-3 bg-muted font-medium text-sm">
                    <div>Source Field</div>
                    <div>Destination Field</div>
                    <div>Transform</div>
                  </div>
                  {config.fieldMappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 p-3 border-t">
                      <Input
                        value={mapping.sourceField}
                        onChange={(e) => {
                          const newMappings = [...config.fieldMappings];
                          newMappings[index].sourceField = e.target.value;
                          setConfig({ ...config, fieldMappings: newMappings });
                        }}
                      />
                      <Input
                        value={mapping.destinationField}
                        onChange={(e) => {
                          const newMappings = [...config.fieldMappings];
                          newMappings[index].destinationField = e.target.value;
                          setConfig({ ...config, fieldMappings: newMappings });
                        }}
                      />
                      <Select
                        value={mapping.transform || 'none'}
                        onValueChange={(value) => {
                          const newMappings = [...config.fieldMappings];
                          newMappings[index].transform = value === 'none' ? '' : value;
                          setConfig({ ...config, fieldMappings: newMappings });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="currency">Currency</SelectItem>
                          <SelectItem value="uppercase">Uppercase</SelectItem>
                          <SelectItem value="lowercase">Lowercase</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-md space-y-3">
                  <div>
                    <div className="text-sm font-medium">Connection</div>
                    <div className="text-sm text-muted-foreground">
                      {config.serverUrl} / {config.database}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Data to Sync</div>
                    <div className="text-sm text-muted-foreground">
                      {Object.entries(config.dataSyncOptions)
                        .filter(([, v]) => v)
                        .map(([k]) => k)
                        .join(', ')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Sync Frequency</div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {config.syncFrequency}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Field Mappings</div>
                    <div className="text-sm text-muted-foreground">
                      {config.fieldMappings.length} mappings configured
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="startSync" defaultChecked />
                  <Label htmlFor="startSync" className="font-normal cursor-pointer">
                    Start initial sync immediately after connection
                  </Label>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !isStep1Valid()) ||
                (step === 2 && !isStep2Valid())
              }
            >
              Next
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={connecting}>
              {connecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
